<script setup>
import { computed, ref, onMounted } from 'vue';
import Auth from 'dashboard/api/auth';
import CampaignsAPI from 'dashboard/api/campaigns';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import Avatar from 'next/avatar/Avatar.vue';
import Icon from 'next/icon/Icon.vue';
import SidebarProfileMenuStatus from './SidebarProfileMenuStatus.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSeparator,
  DropdownItem,
} from 'next/dropdown-menu/base';
import CustomBrandPolicyWrapper from '../../components/CustomBrandPolicyWrapper.vue';

defineProps({
  isCollapsed: { type: Boolean, default: false },
  // 'sidebar' = trigger com nome/email e dropdown abrindo pra cima (rodapé do
  // menu). 'topbar' = trigger compacto (avatar + nome + chevron) e dropdown
  // abrindo pra baixo, alinhado à direita.
  variant: { type: String, default: 'sidebar' },
});

const emit = defineEmits(['close', 'openKeyShortcutModal']);

defineOptions({
  inheritAttrs: false,
});

const { t } = useI18n();

const currentUser = useMapGetter('getCurrentUser');
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const accountId = useMapGetter('getCurrentAccountId');
const globalConfig = useMapGetter('globalConfig/get');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const sortedCurrentUserAccounts = computed(() => {
  return [...(currentUser.value.accounts || [])].sort((a, b) =>
    a.name.localeCompare(b.name)
  );
});

// Só as empresas ATIVAS (account.status === 'active') aparecem para troca.
const activeAccounts = computed(() =>
  sortedCurrentUserAccounts.value.filter(account => account.status === 'active')
);

const showAccountSwitcher = computed(() => activeAccounts.value.length > 1);

// Popup de troca de empresa (com busca para quando houver muitas).
const companyDialogRef = ref(null);
const companySearch = ref('');
const filteredActiveAccounts = computed(() => {
  const query = companySearch.value.trim().toLowerCase();
  if (!query) return activeAccounts.value;
  return activeAccounts.value.filter(account =>
    account.name.toLowerCase().includes(query)
  );
});
const openCompanySwitcher = () => {
  companySearch.value = '';
  companyDialogRef.value?.open();
};

const onChangeAccount = newId => {
  const accountUrl = `/app/accounts/${newId}/dashboard`;
  window.location.href = accountUrl;
};

const showChatSupport = computed(() => {
  return (
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.CONTACT_CHATWOOT_SUPPORT_TEAM
    ) && globalConfig.value.chatwootInboxToken
  );
});

const menuItems = computed(() => {
  return [
    {
      show: showChatSupport.value,
      showOnCustomBrandedInstance: false,
      label: t('SIDEBAR_ITEMS.CONTACT_SUPPORT'),
      icon: 'i-lucide-life-buoy',
      click: () => {
        window.$chatwoot.toggle();
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.KEYBOARD_SHORTCUTS'),
      icon: 'i-lucide-keyboard',
      click: () => {
        emit('openKeyShortcutModal');
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.PROFILE_SETTINGS'),
      icon: 'i-lucide-user-pen',
      link: { name: 'profile_settings_index' },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.APPEARANCE'),
      icon: 'i-lucide-palette',
      click: () => {
        const ninja = document.querySelector('ninja-keys');
        ninja.open({ parent: 'appearance_settings' });
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: false,
      label: t('SIDEBAR_ITEMS.DOCS'),
      icon: 'i-lucide-book',
      link: 'https://www.chatwoot.com/hc/user-guide/en',
      nativeLink: true,
      target: '_blank',
    },
    {
      show: true,
      showOnCustomBrandedInstance: false,
      label: t('SIDEBAR_ITEMS.CHANGELOG'),
      icon: 'i-lucide-scroll-text',
      link: 'https://www.chatwoot.com/changelog/',
      nativeLink: true,
      target: '_blank',
    },
    {
      show: currentUser.value.type === 'SuperAdmin',
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.SUPER_ADMIN_CONSOLE'),
      icon: 'i-lucide-castle',
      link: '/super_admin',
      nativeLink: true,
      target: '_blank',
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.LOGOUT'),
      icon: 'i-lucide-power',
      click: Auth.logout,
    },
  ];
});

const allowedMenuItems = computed(() => {
  return menuItems.value.filter(item => item.show);
});

// Saldo da carteira (R$) — movido do rodapé da sidebar para o perfil.
const walletBrl = ref(null);
const walletLabel = computed(() =>
  walletBrl.value == null
    ? '—'
    : new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
      }).format(walletBrl.value)
);
onMounted(async () => {
  try {
    const { data } = await CampaignsAPI.getCampaignUsage();
    walletBrl.value = data.credits?.available_brl ?? 0;
  } catch {
    walletBrl.value = null;
  }
});
</script>

<template>
  <DropdownContainer
    class="relative min-w-0"
    :class="variant === 'topbar' ? 'w-auto topbar-profile-menu' : 'w-full'"
    @close="emit('close')"
  >
    <template #trigger="{ toggle, isOpen }">
      <button
        class="flex gap-2 items-center p-1 text-left rounded-lg cursor-pointer hover:bg-n-alpha-1"
        :class="[
          { 'bg-n-alpha-1': isOpen },
          variant === 'topbar'
            ? 'ltr:pr-2 rtl:pl-2'
            : isCollapsed
              ? 'w-10 justify-center'
              : 'w-full',
        ]"
        :title="currentUser.available_name"
        @click="toggle"
      >
        <Avatar
          :size="variant === 'topbar' ? 30 : 32"
          :name="currentUser.available_name"
          :src="currentUser.avatar_url"
          :status="currentUserAvailability"
          class="flex-shrink-0"
          rounded-full
        />
        <div v-if="variant === 'sidebar' && !isCollapsed" class="min-w-0">
          <div class="text-sm font-medium leading-4 truncate text-n-slate-12">
            {{ currentUser.available_name }}
          </div>
          <div class="text-xs truncate text-n-slate-11">
            {{ currentUser.email }}
          </div>
        </div>
        <template v-if="variant === 'topbar'">
          <span
            class="order-first hidden sm:block text-sm font-medium truncate text-n-slate-12 max-w-[12rem] ltr:pl-1 rtl:pr-1"
          >
            {{ currentUser.available_name }}
          </span>
          <Icon
            icon="i-lucide-chevron-down"
            class="flex-shrink-0 size-4 text-n-slate-10"
          />
        </template>
      </button>
    </template>
    <DropdownBody
      class="z-50 w-80"
      :class="
        variant === 'topbar'
          ? 'top-full mt-1 ltr:right-0 rtl:left-0'
          : 'bottom-12 mb-2 ltr:left-0 rtl:right-0'
      "
    >
      <SidebarProfileMenuStatus />
      <DropdownSeparator />
      <div class="flex items-center justify-between gap-2 px-3 py-2">
        <span class="flex gap-2 items-center text-sm text-n-slate-11">
          <span class="i-lucide-wallet size-4 text-n-teal-11" />
          {{ t('SIDEBAR_ITEMS.WALLET_BALANCE') }}
        </span>
        <span class="text-sm font-semibold text-n-slate-12">
          {{ walletLabel }}
        </span>
      </div>
      <DropdownSeparator />
      <DropdownItem
        v-if="showAccountSwitcher"
        :label="t('SIDEBAR_ITEMS.SWITCH_COMPANY')"
        icon="i-lucide-arrow-left-right"
        class="cursor-pointer"
        @click="openCompanySwitcher"
      />
      <DropdownSeparator v-if="showAccountSwitcher" />
      <template v-for="item in allowedMenuItems" :key="item.label">
        <CustomBrandPolicyWrapper
          :show-on-custom-branded-instance="item.showOnCustomBrandedInstance"
        >
          <DropdownItem v-if="item.show" v-bind="item" />
        </CustomBrandPolicyWrapper>
      </template>
    </DropdownBody>
  </DropdownContainer>

  <Dialog
    ref="companyDialogRef"
    :title="t('SIDEBAR_ITEMS.SWITCH_COMPANY')"
    :show-confirm-button="false"
    :show-cancel-button="false"
    width="sm"
  >
    <div class="flex flex-col gap-2">
      <Input
        v-model="companySearch"
        :placeholder="t('SIDEBAR_ITEMS.SWITCH_COMPANY_SEARCH')"
      />
      <ul class="flex flex-col gap-1 max-h-80 overflow-y-auto">
        <li v-for="account in filteredActiveAccounts" :key="account.id">
          <button
            class="flex items-center justify-between w-full gap-2 px-3 py-2 text-left rounded-lg hover:bg-n-alpha-1"
            @click="onChangeAccount(account.id)"
          >
            <span
              class="text-sm font-medium truncate text-n-slate-12"
              :title="account.name"
            >
              {{ account.name }}
            </span>
            <Icon
              v-if="account.id === accountId"
              icon="i-lucide-check"
              class="flex-shrink-0 text-n-teal-11 size-4"
            />
          </button>
        </li>
        <li
          v-if="filteredActiveAccounts.length === 0"
          class="px-3 py-2 text-sm text-n-slate-10"
        >
          {{ t('SIDEBAR_ITEMS.SWITCH_COMPANY_EMPTY') }}
        </li>
      </ul>
    </div>
  </Dialog>
</template>

<style scoped>
/* Variante topbar: o DropdownContainer envolve o corpo num wrapper posicionado
   de largura zero ancorado à esquerda, então `right-0` no corpo gruda errado.
   Aqui damos largura total só a esse wrapper (via :deep, sem tocar no
   componente base) para o `right-0` alinhar pela direita do trigger. */
.topbar-profile-menu > :deep(.absolute) {
  width: 100%;
}
</style>
